import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Zap,
  FileText,
  BookOpen,
  PoundSterling,
  ArrowRight,
  HardHat,
  Clock,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/for-small-builders";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Estimating Software for Small Builders",
  "description": "Estimating and quoting software built for small builders in the UK. Generate accurate, professional quotes from drawings, photos or a description in under 10 minutes.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "For Small Builders", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best estimating software for small builders in the UK?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK small builders, The Proper Tool is purpose-built for the job. It generates a fully itemised quote from drawings, photos, or a plain-text description in under 10 minutes. It uses a 519-item UK cost library across 31 trade categories, covers multi-trade residential jobs, and exports branded PDFs. Plans start at £29/month with a 14-day free trial and no credit card required.",
      },
    },
    {
      "@type": "Question",
      "name": "Can a small builder use AI quoting software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is designed for builders of all sizes, including small firms without a dedicated estimator. You describe the job, upload drawings or photos, and the AI generates a fully itemised scope with UK rates. You review, adjust, and send. The whole process typically takes under 10 minutes.",
      },
    },
    {
      "@type": "Question",
      "name": "How does estimating software help small builders avoid underpricing?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool prices every line item from a 519-item UK cost library with Low, Typical, and High rate bands. A built-in validation pipeline flags scope gaps and pricing outliers before you send. The Pricing Accuracy Index also tracks which quotes you win and adjusts future quotes toward your accepted rates, so underpricing becomes rarer over time.",
      },
    },
    {
      "@type": "Question",
      "name": "Does quoting software work for multi-trade jobs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool covers 31 trade categories in a single quote: groundworks, steelwork, carpentry, plastering, M&E, roofing, drainage, and more. A small builder running multi-trade residential projects gets a fully itemised breakdown across all trades in one document.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there free estimating software for small builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start at £29/month. Fully free tools exist but typically lack UK-specific pricing, multi-trade coverage, or professional PDF export.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "What is the best estimating software for small builders in the UK?",
    a: "For UK small builders, The Proper Tool is purpose-built for the job. It generates a fully itemised quote from drawings, photos, or a plain-text description in under 10 minutes. It uses a 519-item UK cost library across 31 trade categories, covers multi-trade residential jobs, and exports branded PDFs. Plans start at £29/month with a 14-day free trial and no credit card required.",
  },
  {
    q: "Can a small builder use AI quoting software?",
    a: "Yes. The Proper Tool is designed for builders of all sizes, including small firms without a dedicated estimator. You describe the job, upload drawings or photos, and the AI generates a fully itemised scope with UK rates. You review, adjust, and send. The whole process typically takes under 10 minutes.",
  },
  {
    q: "How does estimating software help small builders avoid underpricing?",
    a: "The Proper Tool prices every line item from a 519-item UK cost library with Low, Typical, and High rate bands. A built-in validation pipeline flags scope gaps and pricing outliers before you send. The Pricing Accuracy Index also tracks which quotes you win and adjusts future quotes toward your accepted rates, so underpricing becomes rarer over time.",
  },
  {
    q: "Does quoting software work for multi-trade jobs?",
    a: "Yes. The Proper Tool covers 31 trade categories in a single quote: groundworks, steelwork, carpentry, plastering, M&E, roofing, drainage, and more. A small builder running multi-trade residential projects gets a fully itemised breakdown across all trades in one document.",
  },
  {
    q: "Is there free estimating software for small builders?",
    a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start at £29/month.",
  },
];

const FEATURES = [
  {
    icon: Zap,
    title: "Quote in under 10 minutes",
    description: "No spreadsheet setup, no rate research. Describe the job or upload your drawings, and the AI builds a fully itemised scope with UK rates applied.",
  },
  {
    icon: FileText,
    title: "Reads structural drawings",
    description: "Upload architect's drawings or structural plans as PDFs. The AI extracts beam sizes, spans, floor areas, and connection details to price the job accurately.",
  },
  {
    icon: BookOpen,
    title: "31 trade categories",
    description: "From groundworks and steelwork to M&E and landscaping: every common residential trade is covered in a single quote. No separate estimators needed.",
  },
  {
    icon: PoundSterling,
    title: "519-item UK cost library",
    description: "Real UK rates across 31 categories, with Low, Typical, and High bands built in. Override any rate with your own figures to match your market.",
  },
  {
    icon: HardHat,
    title: "Expert scope validation",
    description: "Built-in validation flags scope gaps and pricing outliers before you send, so you catch missed items before they cost you money on site.",
  },
  {
    icon: Clock,
    title: "Pricing that learns your rates",
    description: "The Pricing Accuracy Index tracks your accepted quotes and calibrates future estimates toward your actual margins, so every quote gets more accurate.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Describe the job or upload drawings",
    description: "Type a plain-text description of the project, upload architect's drawings or structural plans, attach site photos, or any combination. Work from whatever you have on site or in the office.",
  },
  {
    step: "2",
    title: "AI generates the itemised scope",
    description: "The AI reads your inputs and produces a fully itemised scope: every trade, every line item, quantities and UK rates across all 31 categories. Typically done in under 10 minutes.",
  },
  {
    step: "3",
    title: "Review, adjust, and send",
    description: "Review every line, apply or dismiss validation flags, add or remove items, then export a branded PDF to send directly to your client.",
  },
];

export default function ForSmallBuildersPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quoting Software for Small Builders UK | The Proper Tool"
        description="Estimating software built for small builders in the UK. Generate accurate, professional quotes from drawings, photos or a description in under 10 minutes. No estimator needed. From £29/mo."
        canonical="/for-small-builders"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>For Small Builders</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Quoting software for small builders UK
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              No estimator on the payroll? No problem. The Proper Tool generates a fully itemised, professionally priced quote from your drawings, photos, or a plain-text description. All in under 10 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/ai-quoting-software">How Smart Quoting works</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Pain points */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">The small builder's quoting problem</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              For small builders, quoting is the job nobody budgets for. You spend hours pricing a job: measuring up, researching rates, building a spreadsheet, formatting a PDF. Then you send it and win maybe one in three. That's unpaid work.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Miss a trade in the scope, and you're absorbing the cost on site. Price too high, and you lose the job to a competitor who guessed lower. Price too low, and you're working at a loss for three months.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The Proper Tool solves all three problems at once: it's fast, it covers every trade, and it prices from real UK rates, so the number you send is a number you can defend.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">How it works</h2>
              <p className="text-lg text-muted-foreground">Three steps from job description to client-ready quote.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.step} className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="features">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything a small builder needs to quote faster</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Built for UK residential construction, not adapted from a generic platform built for larger firms.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-background rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10 min</p>
                <p className="text-muted-foreground">Average time to generate a full quote from a description or drawings</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">519</p>
                <p className="text-muted-foreground">UK construction rates across 31 trade categories</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">£29/mo</p>
                <p className="text-muted-foreground">Starting price: less than one hour of your time</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, transparent pricing</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {([
                { name: "Lite", price: "£29", desc: "For sole traders & small builders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 31-category cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing small firms", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Accuracy Index", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume builders", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
                { href: "/for-sole-traders", label: "For Sole Traders" },
                { href: "/quoting-software-for-contractors", label: "Quoting Software for Contractors" },
                { href: "/estimating-software-for-builders", label: "Estimating Software for Builders" },
                { href: "/auto-quote", label: "Auto Quote: under 10 minutes" },
                { href: "/pricing", label: "Pricing & Plans" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote faster?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join small builders using The Proper Tool to generate professional quotes in minutes, not hours.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
